import { createSignal, Match, onMount, Switch } from 'solid-js'
import type { UsuarioPublico } from '@shared/types'
import { aoEventoNucleo, api, iniciarNucleo, pedirPersistencia } from './nucleo/cliente'
import type { InfoBanco } from './nucleo/worker'
import { Login } from './telas/Login'
import { Casca } from './casca/Casca'
import { mensagemErro } from './lib/erro'
import { emEnderecoAntigo, ENDERECO, irParaEnderecoAtual } from './endereco'
import { ImperativeDialogHost } from '@ui/components/ImperativeDialog'

type Estado =
  | { fase: 'abrindo' }
  | { fase: 'mudando' }
  | { fase: 'outra-aba' }
  | { fase: 'erro'; mensagem: string }
  | { fase: 'login'; banco: InfoBanco }
  | { fase: 'app'; banco: InfoBanco; usuario: UsuarioPublico }

export function App() {
  const [estado, setEstado] = createSignal<Estado>({ fase: 'abrindo' })

  onMount(async () => {
    const parar = aoEventoNucleo((e) => {
      if (e === 'aguardando-outra-aba') setEstado({ fase: 'outra-aba' })
    })
    try {
      const banco = await iniciarNucleo()
      if (emEnderecoAntigo()) {
        setEstado({ fase: 'mudando' })
        if (await irParaEnderecoAtual()) return
      }
      void pedirPersistencia()
      setEstado({ fase: 'login', banco })
    } catch (e) {
      setEstado({ fase: 'erro', mensagem: mensagemErro(e) })
    } finally {
      parar()
    }
  })

  const banco = () => {
    const s = estado()
    return 'banco' in s ? s.banco : null
  }

  return (
    <>
    {/* diálogos abertos por código (confirmar(), kit.tsx) */}
    <ImperativeDialogHost />
    <Switch>
      <Match when={estado().fase === 'abrindo'}>
        <Aviso titulo="Abrindo o Werc…" texto="Preparando o banco local." />
      </Match>
      <Match when={estado().fase === 'mudando'}>
        <Aviso titulo="O Werc mudou de endereço" texto={`Enviando o que está salvo neste computador e abrindo ${ENDERECO}…`} />
      </Match>
      <Match when={estado().fase === 'outra-aba'}>
        <Aviso
          titulo="O Werc já está aberto em outra aba"
          texto="Para proteger os dados, só uma janela usa o banco local por vez. Feche a outra aba e esta abre sozinha."
        />
      </Match>
      <Match when={estado().fase === 'erro'}>
        <Aviso titulo="Não foi possível abrir o banco local" texto={(estado() as { mensagem: string }).mensagem} />
      </Match>
      <Match when={estado().fase === 'login'}>
        <Login aoEntrar={(usuario) => setEstado({ fase: 'app', banco: banco()!, usuario })} />
      </Match>
      <Match when={estado().fase === 'app'}>
        <Casca
          usuario={(estado() as { usuario: UsuarioPublico }).usuario}
          banco={banco()!}
          aoSair={async () => {
            await api.auth.logout()
            setEstado({ fase: 'login', banco: banco()! })
          }}
        />
      </Match>
    </Switch>
    </>
  )
}

function Aviso(props: { titulo: string; texto: string }) {
  return (
    <div class="min-h-dvh flex items-center justify-center bg-page p-6 text-center">
      <div class="max-w-md flex flex-col gap-2">
        <div class="text-lg font-semibold text-ink">{props.titulo}</div>
        <p class="text-sm text-ink-subtle">{props.texto}</p>
      </div>
    </div>
  )
}
